import { useState } from 'react'
import type { JSX } from 'react'

import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_MAX_LENGTH,
  FeedbackError,
  normaliseFeedback,
  type FeedbackCategory,
} from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Telling ANUBRIS what is wrong, or right, with the product. §6.34 `OPS-206`: "In-product
 * feedback capture, routed to the admin portal."
 *
 * **Every limit is met here before the write is queued.** The server refuses a blank message, an
 * unknown category and anything over {@link FEEDBACK_MAX_LENGTH}, and a refused write stops the
 * queue behind it until somebody discards it (`OFF-001a`). So the form runs `normaliseFeedback`,
 * the same function the service calls, and says in a sentence what to change instead of sending a
 * write that will come back refused.
 *
 * **It says not to include customer details, on the page and before the box.** The note lands in
 * front of an operator, and the operator holds no consent to read a customer record
 * (`ADM-005`). The notice is the only control on that: nothing here can tell a customer's name
 * from a word.
 *
 * **Write only.** A shop does not read its notes back in v1.
 */

const CATEGORY_LABEL: Readonly<Record<FeedbackCategory, MessageKey>> = {
  bug: 'feedback.category.bug',
  idea: 'feedback.category.idea',
  question: 'feedback.category.question',
  praise: 'feedback.category.praise',
}

export function Feedback(): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [category, setCategory] = useState<FeedbackCategory>('idea')
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [thanked, setThanked] = useState(false)

  useWriteSettled(snapshot, writeId, () => {
    // Only now is it the server's, so only now is the form cleared and the thanks shown.
    setWriteId(null)
    setBody('')
    setThanked(true)
  })

  const send = async (): Promise<void> => {
    setThanked(false)
    try {
      normaliseFeedback({ category, body })
    } catch (caught) {
      if (!(caught instanceof FeedbackError)) throw caught
      setError(
        caught.code === 'FEEDBACK_TOO_LONG'
          ? t('feedback.tooLong', { max: FEEDBACK_MAX_LENGTH })
          : t('feedback.empty'),
      )
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/feedback',
      label: t('feedback.queueLabel'),
      body: { category, body: body.trim() },
    })) as { id: string }
    setWriteId(write.id)
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.feedback'))
  const over = body.trim().length > FEEDBACK_MAX_LENGTH

  return (
    <section className="lm-feedback" aria-labelledby="feedback-heading">
      <h2 id="feedback-heading" className="lm-feedback__heading">
        {t('feedback.title')}
      </h2>
      <p className="lm-hint">{t('feedback.intro')}</p>
      <p className="lm-feedback__notice" role="note">
        {t('feedback.noCustomerDetails')}
      </p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('feedback.categoryLabel')}</span>
        <select
          className="lm-input"
          value={category}
          onChange={(e) => {
            setCategory(e.currentTarget.value as FeedbackCategory)
          }}
        >
          {FEEDBACK_CATEGORIES.map((id) => (
            <option key={id} value={id}>
              {t(CATEGORY_LABEL[id])}
            </option>
          ))}
        </select>
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('feedback.messageLabel')}</span>
        <textarea
          className="lm-input"
          rows={6}
          value={body}
          placeholder={t('feedback.placeholder')}
          onChange={(e) => {
            setBody(e.currentTarget.value)
            setThanked(false)
          }}
        />
      </label>
      <p className="lm-feedback__count" aria-live="polite">
        {t('feedback.count', { used: body.trim().length, max: FEEDBACK_MAX_LENGTH })}
      </p>

      {error === null && !over ? null : (
        <p className="lm-error" role="alert">
          {error ?? t('feedback.tooLong', { max: FEEDBACK_MAX_LENGTH })}
        </p>
      )}
      <p className="lm-hint" role="status">
        {line}
      </p>
      {thanked ? (
        <p className="lm-hint" role="status">
          {t('feedback.thanks')}
        </p>
      ) : null}

      <div>
        <button
          type="button"
          className="lm-button"
          onClick={() => {
            void send()
          }}
        >
          {t('feedback.submit')}
        </button>
      </div>
    </section>
  )
}
